// The kill switch for single pieces of content, same syntax as the app's
// Remote Config `content_blocklist` (lib/Core/services/content_blocklist.dart),
// so a value can be copied over 1:1. On the web it comes from the worker
// variable CONTENT_BLOCKLIST via /api/config.
//
//   cat:<game_id>/<category>   hides a pack
//   theme:<game_id>/<theme_id> hides one theme
//   img:<game_id>/<theme|pack|*> drops artwork, the theme plays as a word list
//   term:<word>                hides items naming the word (English text counts)

import { GAME_ID, itemName, type Theme } from './themes';

export interface Blocklist {
  categories: Set<string>;
  themes: Set<string>;
  images: Set<string>;
  terms: Set<string>;
}

const TOKEN = /(?:^|[\s,;])(cat|theme|img|term):/gi;

const game = (id: string) => {
  const g = id.trim().toLowerCase();
  return g.startsWith('game_') ? g : `game_${g}`;
};

const key = (name: string) =>
  name.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();

const pair = (gameId: string, k: string) => `${game(gameId)}/${k === '*' ? '*' : key(k)}`;

export const termKey = (word: string) =>
  word.toLowerCase().replace(/[‘’ʼ]/g, "'").replace(/\s+/g, ' ').trim();

export function parseBlocklist(raw: string): Blocklist {
  const out: Blocklist = { categories: new Set(), themes: new Set(), images: new Set(), terms: new Set() };
  const matches = [...raw.matchAll(TOKEN)];
  matches.forEach((m, i) => {
    const end = i + 1 < matches.length ? matches[i + 1].index! : raw.length;
    const value = raw.slice(m.index! + m[0].length, end);
    const kind = m[1].toLowerCase();
    if (kind === 'term') {
      const term = termKey(value.split(/[,;\r\n]/)[0]);
      if (term) out.terms.add(term);
      return;
    }
    const p = value.trim().split(/[\s,;]+/)[0];
    const slash = p.indexOf('/');
    if (slash <= 0 || slash === p.length - 1) return;
    const k = pair(p.slice(0, slash), p.slice(slash + 1));
    if (kind === 'cat') out.categories.add(k);
    else if (kind === 'theme') out.themes.add(k);
    else if (kind === 'img') out.images.add(k);
  });
  return out;
}

export const hidesCategory = (b: Blocklist, category: string) => b.categories.has(pair(GAME_ID, category));

export const hidesTheme = (b: Blocklist, t: Theme) =>
  hidesCategory(b, t.category) || b.themes.has(pair(GAME_ID, t.id));

export function hidesImages(b: Blocklist, t: Theme): boolean {
  if (b.images.size === 0) return false;
  return (
    b.images.has(pair(GAME_ID, '*')) || b.images.has(pair(GAME_ID, t.id)) || b.images.has(pair(GAME_ID, t.category))
  );
}

function termPattern(b: Blocklist): RegExp | null {
  if (b.terms.size === 0) return null;
  const esc = [...b.terms].map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(`(?<![\\p{L}\\p{N}])(?:${esc.join('|')})(?![\\p{L}\\p{N}])`, 'u');
}

/// Indices of [theme]'s items a `term:` hides, matched on the item and on
/// its English twin.
export function hiddenItems(b: Blocklist, theme: Theme, english?: Theme): Set<number> {
  const re = termPattern(b);
  const out = new Set<number>();
  if (!re) return out;
  const paired = english && english.items.length === theme.items.length ? english : null;
  theme.items.forEach((it, i) => {
    if (re.test(termKey(itemName(it))) || (paired && re.test(termKey(itemName(paired.items[i]))))) out.add(i);
  });
  return out;
}
